import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {PositiveUnits} from '@mintdata/mint-kit/api/models/PositiveUnits';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {PositiveUnitsConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/PositiveUnitsConfig';

export class BorderRadius
{
    @Property<PositiveUnits>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: PositiveUnitsConfig,
        typeCtor: PositiveUnits
    })
    public topLeft:PositiveUnits = PositiveUnits.fromPixels(0);

    @Property<PositiveUnits>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: PositiveUnitsConfig,
        typeCtor: PositiveUnits
    })
    public topRight:PositiveUnits = PositiveUnits.fromPixels(0);

    @Property<PositiveUnits>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: PositiveUnitsConfig,
        typeCtor: PositiveUnits
    })
    public bottomLeft:PositiveUnits = PositiveUnits.fromPixels(0);

    @Property<PositiveUnits>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: PositiveUnitsConfig,
        typeCtor: PositiveUnits
    })
    public bottomRight:PositiveUnits = PositiveUnits.fromPixels(0);

    public constructor(
        topLeft,
        topRight,
        bottomLeft,
        bottomRight
    )
    {
        this.topLeft = topLeft;
        this.topRight = topRight;
        this.bottomLeft = bottomLeft;
        this.bottomRight = bottomRight;
    }

    @Property<PositiveUnits>({
        title: '',
        tip: '',
        isRequired: true,
        typeConfig: PositiveUnitsConfig,
        typeCtor: PositiveUnits
    })
    public get value():PositiveUnits
    {
        return this._areValuesEqual()
            ? this.topLeft
            : null;
    }

    public set value(value:PositiveUnits)
    {
        this.topLeft = value;
        this.topRight = value;
        this.bottomLeft = value;
        this.bottomRight = value;
    }

    private _areValuesEqual():boolean
    {
        return (!this.topLeft
                && !this.topRight
                && !this.bottomLeft
                && !this.bottomRight
            )
            || (this.topLeft.isEqualTo(this.topRight)
                && this.topLeft.isEqualTo(this.bottomLeft)
                && this.topLeft.isEqualTo(this.bottomRight));
    }

    public isEqualTo(other:BorderRadius):boolean
    {
        return other
            && other.topLeft
            && other.topLeft.isEqualTo(this.topLeft)
            && other.topRight
            && other.topRight.isEqualTo(this.topRight)
            && other.bottomLeft
            && other.bottomLeft.isEqualTo(this.bottomLeft)
            && other.bottomRight
            && other.bottomRight.isEqualTo(this.bottomRight);
    }

    public static fromPixels(
        topLeft:number,
        topRight:number,
        bottomLeft:number,
        bottomRight:number
    ):BorderRadius
    {
        return new BorderRadius(
            PositiveUnits.fromPixels(topLeft),
            PositiveUnits.fromPixels(topRight),
            PositiveUnits.fromPixels(bottomLeft),
            PositiveUnits.fromPixels(bottomRight),
        );
    }

    public static fromEqualPixels(
        value:number
    ):BorderRadius
    {
        return new BorderRadius(
            PositiveUnits.fromPixels(value),
            PositiveUnits.fromPixels(value),
            PositiveUnits.fromPixels(value),
            PositiveUnits.fromPixels(value),
        );
    }

    public static coerce(from:IValue):BorderRadius | null
    {
        if( from.getType().isEqualTo(Type.Number)
            || from.getType().isEqualTo(Type.Integer) )
        {
            const value:number = parseFloat((from as any).value);
            return BorderRadius.fromEqualPixels(value);
        }
        else if( from.getType().isEqualTo(Type.String) )
        {
            const value:PositiveUnits = PositiveUnits.fromPositiveUnitsString(from as AString);
            return new BorderRadius(value, value, value, value);
        }

        return null;
    }
}
